import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Tooltip } from '@mui/material';
import { useLoanActionRunnerContext } from 'app/pages/loanApplication/LoanHeaders/LoanInfoHeader/LoanActionsControls';
import ActionButton from 'app/pages/loanApplication/LoanHeaders/RedesignedLoanInfoHeader/ActionButton';
import { useTaskActionRunner } from 'app/shared/modules/TaskActionRunner';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { ConfirmationModalType } from 'constants/confirmationDialog';
import { hasMersPreviewItem } from 'utils/loanActions';
import { getTaskActionAvailability } from 'utils/taskTypeActions';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { openConfirmationDialogAction } from 'store/global/actions';

import { tooltipSlotProps } from '../../TaskInfo.styles';
import { TaskInfoTestIds } from '../../TaskInfo.testIds';

import {
  TASK_ACTION_CONFIRM_BUTTON_NAME,
  TASK_ACTION_CONFIRMATION_MESSAGE,
} from './TaskActionButton.constants';
import { getTaskActionConfirmationDescription } from './TaskActionButton.utils';

/**
 * The task type's action button in the Task Info footer. Renders only under a TaskActionRunner
 * host; the run flow (and anything it opens) lives in that host.
 */
const TaskActionButton = ({ task, isHidden = false }) => {
  const dispatch = useDispatch();

  const taskActionRunner = useTaskActionRunner();
  const { isLoading, executingActionId, disabledLoanActionsSet, onLoanActionClick } =
    useLoanActionRunnerContext();
  const isApproveAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.APPROVE_REJECT_CANCEL_TASK),
  );

  const taskTypeAction = taskActionRunner?.taskTypeAction ?? null;
  const taskTypeId = taskTypeAction?.taskTypeId ?? null;
  const isRunning = isLoading || !!executingActionId || !!taskActionRunner?.isPending;

  const { isVisible, isDisabled, disabledReason } = getTaskActionAvailability({
    status: task.status,
    onSuccessTaskStatus: taskTypeAction?.onSuccessTaskStatus ?? null,
    taskDataType: taskActionRunner?.taskDataType ?? null,
    isApproveAllowed,
    isUnavailableForLoan: !!taskTypeId && disabledLoanActionsSet.has(taskTypeId),
    isReady: !!taskActionRunner?.isReady,
    isLoadFailed: !!taskActionRunner?.isLoadFailed,
    isCompleted: !!taskActionRunner?.isCompleted,
  });

  const isMersPreviewAction = hasMersPreviewItem(taskTypeAction?.loanActionTypes);

  // A Preview MERS run sends nothing, so it runs without asking first.
  const onClick = useCallback(() => {
    if (isMersPreviewAction) {
      onLoanActionClick(taskTypeId);

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: TASK_ACTION_CONFIRMATION_MESSAGE,
        description: getTaskActionConfirmationDescription(taskTypeAction?.buttonLabel ?? ''),
        onAccept: () => onLoanActionClick(taskTypeId),
        onAcceptButtonName: TASK_ACTION_CONFIRM_BUTTON_NAME,
      }),
    );
  }, [dispatch, isMersPreviewAction, onLoanActionClick, taskTypeAction?.buttonLabel, taskTypeId]);

  if (!taskTypeAction || isHidden || !isVisible) {
    return null;
  }

  return (
    <Tooltip placement="top" slotProps={tooltipSlotProps} title={disabledReason ?? ''}>
      <span className="mr-auto">
        <ActionButton
          color={taskTypeAction.color ?? null}
          dataTestId={TaskInfoTestIds.TASK_INFO_FORM_TASK_ACTION_BUTTON}
          disabled={isDisabled || isRunning}
          icon={taskTypeAction.icon ?? null}
          isCompact={false}
          name={taskTypeAction.buttonLabel}
          variant={taskTypeAction.variant ?? null}
          onClick={onClick}
        >
          {taskTypeAction.buttonLabel}
        </ActionButton>
      </span>
    </Tooltip>
  );
};

TaskActionButton.propTypes = {
  task: PropTypes.shape({
    status: PropTypes.string.isRequired,
  }).isRequired,
  isHidden: PropTypes.bool,
};

export default memo(TaskActionButton);
